/**
 * Owner: Masa Bayu
 * File: QxBookCard.tsx
 * Date:Time: 2026-09-28 12:11
 * Description: Book card — cover · title · description · price · no section reason.
 */

import type { KeyboardEvent } from 'react';
import type { QxreadBookCard, QxreadRecommendationReason } from '@qx/qxread-core';
import { resolveDemoCoverUrl } from '../publication/demo-cover-assets';

export interface QxBookCardProps {
  readonly card: QxreadBookCard;
  readonly onNavigate: (path: string) => void;
  readonly idPrefix?: string;
  readonly reason?: QxreadRecommendationReason;
  readonly variant?: 'list' | 'cover';
  readonly priceLabel?: string;
  /** Desktop workspace only — hover Quick View affordance. */
  readonly quickView?: boolean;
}

const COVER_TONES = ['ink', 'sea', 'rose', 'sand', 'slate', 'forest'] as const;

function coverTone(publicationId: string): (typeof COVER_TONES)[number] {
  let hash = 0;
  for (let i = 0; i < publicationId.length; i += 1) {
    hash = (hash + publicationId.charCodeAt(i) * (i + 1)) % COVER_TONES.length;
  }
  return COVER_TONES[hash] ?? 'ink';
}

function ctaLabel(cta: QxreadBookCard['cta']): string {
  switch (cta) {
    case 'preview_available':
      return 'Sample available';
    case 'coming_soon':
      return 'Coming soon';
    case 'unavailable':
      return 'Unavailable';
    default:
      return 'View details';
  }
}

export function QxBookCard({
  card,
  onNavigate,
  idPrefix = 'card',
  variant = 'cover',
  priceLabel,
  quickView = false,
}: QxBookCardProps) {
  const authors = card.authors.join(', ');
  const href = `/publication/${card.publicationId}`;
  const headingId = `${idPrefix}-${card.publicationId}`;
  const genre = card.tags[0];
  const tone = coverTone(card.publicationId);
  const coverUrl = resolveDemoCoverUrl(card.coverAssetRef);
  const coverCard = variant === 'cover';
  const classes = [
    'qx-book-card',
    'qx-material',
    'qx-material--elevated',
    'qx-motion-hover-lift',
    coverCard ? 'qx-book-card--cover' : null,
    coverCard ? 'qx-book-card--tappable' : null,
    coverCard ? 'qx-motion-press' : null,
    quickView ? 'qx-book-card--quick-view' : null,
    coverUrl !== null ? 'qx-book-card--photo' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const openDetail = () => {
    onNavigate(href);
  };

  const onCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDetail();
    }
  };

  return (
    <article
      className={classes}
      aria-labelledby={headingId}
      data-cover-tone={tone}
      {...(coverCard
        ? {
            role: 'link',
            tabIndex: 0,
            onClick: openDetail,
            onKeyDown: onCardKeyDown,
          }
        : {})}
    >
      <div className="qx-book-card__cover" aria-hidden="true">
        {coverUrl !== null ? (
          <img
            className="qx-book-card__cover-img"
            src={coverUrl}
            alt=""
            decoding="async"
            draggable={false}
          />
        ) : (
          <span className="qx-book-card__cover-title">{card.title}</span>
        )}
      </div>
      <div className="qx-book-card__body">
        {genre !== undefined ? <p className="qx-book-card__genre">{genre}</p> : null}
        <h3 id={headingId}>
          {coverCard ? (
            <span className="qx-book-card__title-text">{card.title}</span>
          ) : (
            <a
              href={href}
              onClick={(event) => {
                event.preventDefault();
                openDetail();
              }}
            >
              {card.title}
            </a>
          )}
        </h3>
        {card.description !== null ? (
          <p className="qx-book-card__description">{card.description}</p>
        ) : null}
        <p className="qx-book-card__meta">
          {authors.length > 0 ? `${authors} · ` : ''}v{card.listedVersion ?? '—'}
        </p>
        {priceLabel !== undefined ? <p className="qx-book-card__price">{priceLabel}</p> : null}
        <p className="qx-book-card__cta" role="note">
          {ctaLabel(card.cta)} · not a purchase
        </p>
      </div>
      {quickView ? (
        <div className="qx-book-card__quick qx-material qx-material--glass">
          <button
            type="button"
            className="qx-book-card__quick-btn qx-motion-press"
            onClick={(event) => {
              event.stopPropagation();
              openDetail();
            }}
          >
            Quick view
          </button>
        </div>
      ) : null}
    </article>
  );
}
